import { RichTextDisplay } from "@datagame/ui";
import { motion, usePresence } from "framer-motion";
import { ReactComponentElement, useEffect, useMemo, useState } from "react";

import type { Attribute } from "../../../gql/graphql";
import { CARD_REVERT_DURATION } from "../../../constants";
import { useLongPress } from "../../../hooks/useLongPress";

import styles from "./Content.module.scss";

export interface ContentProps {
  isReversed: boolean;
  index?: number;
  reverseImage?: string;
  attribute: Attribute;
  overlayClass?: string;
  contentClass?: string;
  shouldLockStylesOnClick?: boolean;
  isDisabled: boolean;
  overlayBackground?: string;
  icon?: ReactComponentElement<"svg">;
  onClick?: () => void;
  onLongPress?: () => void;
}

export const CardContent = (props: ContentProps) => {
  const [preservedStyle, setPreservedStyle] = useState<
    | {
        overlayClass: string;
        contentClass: string;
        icon?: ReactComponentElement<"svg">;
        overlayBackground?: string;
      }
    | undefined
  >();
  const {
    attribute,
    overlayClass = "",
    contentClass = "",
    icon,
    isDisabled,
    isReversed,
    shouldLockStylesOnClick = true,
    onClick = () => null,
    onLongPress = onClick,
    overlayBackground,
    reverseImage,
  } = props;
  const [isPresent, safeToRemove] = usePresence();
  const disabled = isDisabled || !isPresent;
  const initialState = useMemo(() => (isReversed ? "reversed" : "normal"), []);

  const rotateDuration = CARD_REVERT_DURATION / 2;

  const cardImageVariants = {
    reversed: {
      rotateY: 0,
      transition: {
        duration: rotateDuration,
        delay: rotateDuration,
      },
    },
    normal: {
      rotateY: -90,
      transition: {
        duration: rotateDuration,
        delay: CARD_REVERT_DURATION,
      },
    },
  };

  const cardContentVariants = {
    reversed: {
      rotateY: 90,
      transition: {
        duration: rotateDuration,
      },
    },
    normal: {
      rotateY: 0,
      transition: {
        duration: rotateDuration,
        delay: CARD_REVERT_DURATION + rotateDuration,
      },
    },
  };

  useEffect(() => {
    if (!isPresent) {
      safeToRemove();
    }
  }, [isPresent]);

  const handleClick = () => {
    if (disabled) {
      return;
    }
    shouldLockStylesOnClick && setPreservedStyle({ overlayClass, overlayBackground, contentClass, icon });
    onClick();
  };

  const events = useLongPress({
    onClick: handleClick,
    onLongPress: onLongPress,
  });

  const renderContent = () => {
    const imageSection = <img className={styles.cardImage} src={attribute.image?.url} alt={attribute.name} />;
    const textSection = (
      <div className={styles.cardTextWrapper}>
        <RichTextDisplay value={attribute?.customText ?? attribute?.name} className={styles.cardText} />
      </div>
    );

    if (!attribute) {
      return null;
    }

    return (
      <>
        {(!attribute.image || attribute.showImageAndText) && textSection}
        {attribute.image && imageSection}
      </>
    );
  };

  return (
    <motion.div className={styles.wrapper} {...events}>
      <motion.img
        variants={cardImageVariants}
        initial={initialState}
        animate={isReversed ? "reversed" : "normal"}
        className={styles.cardReverseImage}
        src={reverseImage}
        alt="card revers"
      />
      <motion.div
        initial={initialState}
        className={preservedStyle?.contentClass ?? contentClass}
        variants={cardContentVariants}
        animate={isReversed ? "reversed" : "normal"}
      >
        {renderContent()}
        {Boolean(overlayClass.length) && (
          <motion.div
            variants={{
              idle: { opacity: 0, background: preservedStyle?.overlayBackground ?? overlayBackground },
              selected: { opacity: 1, background: preservedStyle?.overlayBackground ?? overlayBackground },
            }}
            // For some reason, framer-motion will not rest hover state if we don't provide that callback
            onHoverEnd={() => 0}
            animate={preservedStyle ? "selected" : "idle"}
            whileHover={disabled ? undefined : { opacity: 1 }}
            className={preservedStyle?.overlayClass ?? overlayClass}
          >
            {preservedStyle?.icon ?? icon}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};
